import { type ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { LanguageSwitcher } from '../LanguageSwitcher';
import './AppLayout.css';

function getPageTitle(t: (key: string) => string, pathname: string): string {
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) {
    return t('nav.dashboard');
  }
  if (pathname === '/resume-tuner') {
    return t('nav.resumeTuner');
  }
  if (pathname === '/job-search') {
    return t('nav.jobSearch');
  }
  if (pathname === '/linkedin-posts') {
    return t('nav.linkedinPosts');
  }
  if (pathname === '/status') {
    return t('nav.status');
  }
  if (pathname === '/resume') {
    return t('nav.resume');
  }
  if (pathname === '/resume-history') {
    return 'Resume History';
  }
  if (pathname === '/career-vision') {
    return t('nav.careerVision');
  }
  if (pathname === '/experience-graph') {
    return t('nav.experienceGraph');
  }
  if (pathname.startsWith('/career')) {
    return t('nav.careerMemory');
  }
  if (pathname.startsWith('/weekly')) {
    return t('nav.weeklyUpdate');
  }
  if (pathname === '/settings') {
    return t('nav.settings');
  }
  if (pathname === '/') {
    return t('nav.home');
  }
  return '';
}

function formatVersionLabel() {
  const version = process.env.REACT_APP_VERSION || '';
  const sha = process.env.REACT_APP_GIT_SHA || '';
  const branch = process.env.REACT_APP_GIT_BRANCH || '';
  const shortSha = sha ? sha.slice(0, 7) : '';
  
  if (branch && shortSha) return `${branch} · ${shortSha}`;
  if (branch) return branch;
  if (version && shortSha) return `v${version} (${shortSha})`;
  if (version) return `v${version}`;
  if (shortSha) return shortSha;
  return '';
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);

  const logoTo = user ? '/dashboard' : '/';
  const pageTitle = getPageTitle(t, location.pathname);
  const versionLabel = formatVersionLabel();

  return (
    <div className="appLayoutRoot">
      <header className="appHeader">
        <div className="appHeaderLeft">
          <Link to={logoTo} className="appHeaderLogo" onClick={() => setMenuOpen(false)}>
            MyJobOdyssey
          </Link>
        </div>

        <div className="appHeaderCenter">
          {pageTitle && <h1 className="appPageTitle">{pageTitle}</h1>}
        </div>

        <div className="appHeaderRight">
          <LanguageSwitcher />
          {user ? (
            <div className="appUserMenu">
              <button
                className="appUserButton"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
              >
                {user.displayName || user.email || 'Account'}
              </button>
              {menuOpen && (
                <div className="appUserDropdown" role="menu">
                  <button
                    className="appUserDropdownItem"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/settings');
                    }}
                    role="menuitem"
                  >
                    {t('nav.settings')}
                  </button>
                  <button
                    className="appUserDropdownItem"
                    onClick={async () => {
                      setMenuOpen(false);
                      await signOut();
                      navigate('/');
                    }}
                    role="menuitem"
                  >
                    {t('common.logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="appHeaderAuthLinks">
              <Link to="/login" className="appHeaderLink">{t('auth.login')}</Link>
              <Link to="/signup" className="appHeaderLink">{t('auth.signup')}</Link>
            </div>
          )}
        </div>
      </header>

      <main className="appMain" onClick={() => setMenuOpen(false)}>
        {children}
      </main>

      <footer className="appFooter">
        <div className="appFooterLinks">
          {user ? (
            <>
              <Link to="/dashboard" className="appFooterLink">{t('nav.dashboard')}</Link>
              <Link to="/resume-tuner" className="appFooterLink">{t('nav.resumeTuner')}</Link>
              <Link to="/job-search" className="appFooterLink">{t('nav.jobSearch')}</Link>
              <Link to="/experience-graph" className="appFooterLink">{t('nav.experienceGraph')}</Link>
              <Link to="/linkedin-posts" className="appFooterLink">{t('nav.linkedinPosts')}</Link>
              <Link to="/status" className="appFooterLink">{t('nav.status')}</Link>
            </>
          ) : (
            <>
              <Link to="/" className="appFooterLink">{t('nav.home')}</Link>
              <Link to="/status" className="appFooterLink">{t('nav.status')}</Link>
            </>
          )}
        </div>
        {versionLabel && <div className="appFooterVersion">{versionLabel}</div>}
      </footer>
    </div>
  );
}
